<template>
  <global-dialog-card
    v-model="model"
    :max-width="1200"
    hide-padding
    :title="$t('menu_selection_description')"
  >
    <v-container fluid class="grey lighten-5 pa-6">
      <v-row class="mt-4">
        <v-col
          v-for="menuItem in availableMenuItems"
          :key="menuItem.title"
          cols="12"
          sm="6"
          md="4"
          xl="3"
        >
          <v-card class="pa-4 permission-card" outlined :hover="true">
            <v-checkbox
              :input-value="isMenuItemSelected(menuItem.title)"
              :label="$t(menuItem.title)"
              :disabled="isMenuItemDisabled(menuItem.title)"
              @change="handleMenuItemChange(menuItem.title, $event)"
              color="primary"
              hide-details
              class="ma-0"
            />
          </v-card>
        </v-col>
      </v-row>
    </v-container>
    <template #actions>
      <v-btn text @click="model = false">
        {{ $t("cancel") }}
      </v-btn>
      <primary-button @click="saveMenuSelection()" :loading="isSavingMenu">
        {{ $t("save") }}
      </primary-button>
    </template>
  </global-dialog-card>
</template>

<script>
import hasModel from "@/mixins/hasModel";
import hasSnackbar from "@/mixins/hasSnackbar";
import userService from "@/services/user";

const AVAILABLE_MENU_ITEMS = [
  { title: "meal_main" },
  { title: "report" },
  { title: "company_reports" },
  { title: "guest_report" },
  { title: "post_report" },
  { title: "multi_company_report" },
  { title: "stock" },
  { title: "companies" },
  { title: "companies_delivery" },
  { title: "suppliers" },
  { title: "meals_main" },
  { title: "groups" },
  { title: "menu_types" },
  { title: "groceri" },
  { title: "settings" },
  { title: "gallery" },
  { title: "restaurants" },
  { title: "users" },
  { title: "customers_restoran" },
  { title: "history" },
  { title: "notifications" },
  { title: "import_user" },
  { title: "recipes" },
  { title: "additional_meals" },
  { title: "limits" },
];

const DEFAULT_MENU_SELECTION = AVAILABLE_MENU_ITEMS.map(({ title }) => title);

const MENU_PRIORITY_ORDER = [
  "meal_main",
  "report",
  "guest_report",
  "multi_company_report",
  "companies",
  "stock",
  "meals_main",
  "groups",
  "menu_types",
  "groceri",
  "gallery",
  "restaurants",
];

export default {
  mixins: [hasModel, hasSnackbar],
  props: {
    employee: {
      type: Object,
      default: () => ({}),
    },
    podesavanja: {
      type: Array,
      default: () => [],
    },
  },
  data: () => ({
    selectedMenuItems: [],
    isSavingMenu: false,
    isUpdatingSettings: false,
    isUpdatingRestaurantAccess: false,
    availableMenuItems: AVAILABLE_MENU_ITEMS,
    menuPriorityOrder: MENU_PRIORITY_ORDER,
  }),
  methods: {
    isSettingsItem(menuKey) {
      return menuKey === "settings";
    },

    isRestaurantItem(menuKey) {
      return menuKey === "restaurants";
    },

    isMenuItemSelected(menuKey) {
      if (this.isSettingsItem(menuKey)) {
        return Boolean(this.employee?.pivot?.settings);
      }

      return this.selectedMenuItems.includes(menuKey);
    },

    isMenuItemDisabled(menuKey) {
      if (this.isSettingsItem(menuKey)) {
        return this.isUpdatingSettings || !this.employee?.id;
      }

      if (this.isRestaurantItem(menuKey)) {
        return this.isUpdatingRestaurantAccess || !this.employee?.id;
      }

      return false;
    },

    setMenuItemSelection(menuKey, isSelected) {
      const isAlreadySelected = this.selectedMenuItems.includes(menuKey);

      if (isSelected && !isAlreadySelected) {
        this.selectedMenuItems = [...this.selectedMenuItems, menuKey];
      }

      if (!isSelected && isAlreadySelected) {
        this.selectedMenuItems = this.selectedMenuItems.filter(
          (selectedMenuKey) => selectedMenuKey !== menuKey
        );
      }
    },

    async handleMenuItemChange(menuKey, isSelected) {
      if (this.isSettingsItem(menuKey)) {
        await this.toggleSettingsAccess(isSelected);
        return;
      }

      if (this.isRestaurantItem(menuKey)) {
        await this.toggleRestaurantAccess(isSelected);
        return;
      }

      this.setMenuItemSelection(menuKey, isSelected);
    },

    async toggleSettingsAccess(isEnabled) {
      if (!this.employee?.id || !this.employee?.pivot) return;

      const previousValue = Boolean(this.employee.pivot.settings);

      this.$set(this.employee.pivot, "settings", isEnabled);
      this.isUpdatingSettings = true;

      try {
        await userService.updateUserSettingsV2({
          user_id: this.employee.id,
          restaurant_id: this.employee.pivot.restaurant_id,
          settings: isEnabled ? 1 : 0,
        });

        this.showSuccessSnackbar(this.$t("settings_updated"));
      } catch (error) {
        console.error("Error updating settings:", error);
        this.showErrorSnackbar(this.$t("error_updating_settings"));
        this.$set(this.employee.pivot, "settings", previousValue);
      } finally {
        this.isUpdatingSettings = false;
      }
    },

    async handleRestaurantChange(employee) {
      this.restaurantLoading = true;
      try {
        const hasRestaurants = this.selectedMenuItems.includes("restaurants");
        await userService.updateUserRestaurantAccess(
          employee.id,
          hasRestaurants,
          this.$route.params.id
        );
        this.showSuccessSnackbar(this.$t("restaurant_access_updated"));
      } catch (error) {
        console.error("Error updating restaurant access:", error);
        this.showErrorSnackbar(this.$t("error_updating_restaurant_access"));
        this.selectedMenuItems = previousSelection;
      } finally {
        this.isUpdatingRestaurantAccess = false;
      }
    },

    sortMenuItems(items) {
      return items.sort((firstItem, secondItem) => {
        const firstPriority = this.menuPriorityOrder.indexOf(firstItem);
        const secondPriority = this.menuPriorityOrder.indexOf(secondItem);

        if (firstPriority !== -1 && secondPriority !== -1) {
          return firstPriority - secondPriority;
        }

        if (firstPriority !== -1) return -1;
        if (secondPriority !== -1) return 1;

        return firstItem.localeCompare(secondItem);
      });
    },

    async saveMenuSelection() {
      if (!this.employee?.id) return;

      this.isSavingMenu = true;

      try {
        const sortedMenuItems = this.sortMenuItems([...this.selectedMenuItems]);

        await userService.updateUserMenu(this.employee.id, sortedMenuItems);

        this.showSuccessSnackbar(this.$t("user_menu_updated"));
        this.model = false;
      } catch (error) {
        console.error("Error saving menu:", error);
        this.showErrorSnackbar(this.$t("error_updating_user_menu"));
      } finally {
        this.isSavingMenu = false;
      }
    },

    parseUserMenuItems(menuString) {
      try {
        return JSON.parse(menuString)
          .map((item) => item.title)
          .filter(Boolean);
      } catch (error) {
        console.error("Error parsing user menu:", error);
        return [];
      }
    },
  },
  watch: {
    employee: {
      handler(employee) {
        if (!employee) {
          this.selectedMenuItems = [];
          return;
        }

        const storedMenuItems = employee.user_menu?.menu1
          ? this.parseUserMenuItems(employee.user_menu.menu1)
          : [];

        this.selectedMenuItems = storedMenuItems.length
          ? storedMenuItems
          : [...DEFAULT_MENU_SELECTION];
      },
      immediate: true,
    },
  },
};
</script>
